import React, { useEffect, useRef, useState } from 'react';

export type DailyNoteLockModalMode = 'set' | 'email' | 'note';

type Props = {
  open: boolean;
  mode: DailyNoteLockModalMode;
  busy?: boolean;
  error?: string;
  emailHint?: string;
  onClose: () => void;
  onSubmit: (value: string, confirmPassword?: string) => void;
};

const COPY: Record<
  DailyNoteLockModalMode,
  { title: string; desc: string; confirmLabel: string; showConfirm: boolean }
> = {
  set: {
    title: '잠금 비밀번호 설정',
    desc: '데일리 노트를 잠그려면 잠금 비밀번호를 설정해 주세요. (4자 이상)',
    confirmLabel: '설정',
    showConfirm: true,
  },
  email: {
    title: '잠금 설정 해제',
    desc: '노트를 보는 암호가 아닙니다. 잠금을 끄려면 이 계정으로 로그인한 이메일을 입력해 주세요.',
    confirmLabel: '확인',
    showConfirm: false,
  },
  note: {
    title: '잠금 비밀번호 입력',
    desc: '데일리 노트를 보려면 설정한 노트 암호를 입력해 주세요.',
    confirmLabel: '확인',
    showConfirm: false,
  },
};

const DailyNoteLockModal: React.FC<Props> = ({
  open,
  mode,
  busy,
  error,
  emailHint,
  onClose,
  onSubmit,
}) => {
  const [value, setValue] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const firstInputRef = useRef<HTMLInputElement>(null);
  const copy = COPY[mode];

  useEffect(() => {
    if (!open) return;
    setValue('');
    setConfirmPassword('');
    const t = window.setTimeout(() => firstInputRef.current?.focus(), 30);
    return () => window.clearTimeout(t);
  }, [open, mode]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onClose]);

  if (!open) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (copy.showConfirm) onSubmit(value, confirmPassword);
    else onSubmit(value);
  };

  const isEmail = mode === 'email';
  const placeholder = isEmail ? '로그인 이메일' : '잠금 비밀번호';

  return (
    <div
      className="cp-modal-backdrop"
      role="presentation"
      onClick={busy ? undefined : onClose}
    >
      <div
        className="cp-modal cp-daily-note-lock-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-daily-note-lock-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="cp-daily-note-lock-title" className="cp-daily-note-lock-title">
          {copy.title}
        </h2>
        <p className="cp-daily-note-lock-desc">{copy.desc}</p>
        {isEmail && emailHint ? (
          <p className="cp-daily-note-lock-hint">
            로그인 이메일{' '}
            <span className="cp-daily-note-lock-hint-mask">{emailHint}</span>
          </p>
        ) : null}
        <form className="cp-daily-note-lock-form" onSubmit={submit}>
          <input
            ref={firstInputRef}
            type={isEmail ? 'email' : 'password'}
            className="cp-daily-note-lock-input"
            placeholder={placeholder}
            inputMode={isEmail ? 'email' : undefined}
            autoComplete={isEmail ? 'username' : mode === 'set' ? 'new-password' : 'off'}
            autoCapitalize={isEmail ? 'none' : undefined}
            spellCheck={isEmail ? false : undefined}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={busy}
          />
          {copy.showConfirm ? (
            <input
              type="password"
              className="cp-daily-note-lock-input"
              placeholder="비밀번호 확인"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={busy}
            />
          ) : null}
          {error ? (
            <p className="cp-daily-note-lock-error" role="alert">
              {error}
            </p>
          ) : null}
          <div className="cp-daily-note-lock-actions">
            <button type="button" className="cp-btn-secondary" onClick={onClose} disabled={busy}>
              취소
            </button>
            <button type="submit" className="cp-btn-primary" disabled={busy}>
              {busy ? '확인 중…' : copy.confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DailyNoteLockModal;
